import { NotificationType } from 'shovel-lib/types';

/**
 * The session pings' desktop notices, from the tab's side (notifications cleanup, Petr 2026-10-31). Sent from the server
 * through Firebase, whose worker shows them while every Shovel tab is hidden; public/session-notice-click.js handles
 * their clicks. The ping's fields travel as Firebase's message data: type, plannedTaskId, taskId and taskType.
 */

const MESSAGE = 'shovel-session-notice';
const FIREBASE_WORKER_SCOPE = '/firebase-cloud-messaging-push-scope';

export type SessionPingData = { type: string; plannedTaskId: string; taskId: string; taskType: string };

export type SessionPing = {
  type: NotificationType;
  payload: { plannedTaskId: string; taskId: string; taskType: string };
};

export const toSessionPing = ({ type, ...payload }: SessionPingData): SessionPing => ({
  type: type as NotificationType,
  payload
});

/**
 * For a ping that reaches a tab that's on screen but not in use, say beside Google Docs: Firebase counts that as in view
 * and leaves it to the tab, where a toast would go unseen. False if the notice couldn't show.
 */
export const showSessionNotice = async (title: string, body: string, session: SessionPingData) => {
  try {
    const worker = await navigator.serviceWorker.getRegistration(FIREBASE_WORKER_SCOPE);
    if (!worker) return false;
    const options: NotificationOptions & { renotify?: boolean } = {
      body,
      icon: '/icons-192x192.png',
      tag: `shovel-session-${session.plannedTaskId}`,
      renotify: true,
      data: { session }
    };
    await worker.showNotification(title, options);
    return true;
  } catch {
    return false;
  }
};

/** A click on a notice, as the worker hands it to this tab. Returns the unsubscribe. */
export const onSessionNoticeClick = (listener: (ping: SessionPing) => void) => {
  if (!('serviceWorker' in navigator)) return () => undefined;
  const handle = (event: MessageEvent) => {
    if (event.data?.type === MESSAGE) listener(toSessionPing(event.data.session));
  };
  navigator.serviceWorker.addEventListener('message', handle);
  return () => navigator.serviceWorker.removeEventListener('message', handle);
};

/** The ping in a web address, from a click with no Shovel tab open; the address is cleaned of it. */
export const readSessionFromAddress = (): SessionPing | null => {
  const params = new URLSearchParams(window.location.search);
  const [type, plannedTaskId, taskId, taskType] = ['type', 'plannedTaskId', 'taskId', 'taskType'].map(key =>
    params.get(key)
  );
  if (!type || !plannedTaskId || !taskId || !taskType) return null;
  window.history.replaceState(window.history.state, '', window.location.pathname);
  return toSessionPing({ type, plannedTaskId, taskId, taskType });
};

// Read as the app loads, before the router sends `/` on to the student's term and drops it.
let sessionFromAddress = readSessionFromAddress();

/** The ping this page was opened for, once. */
export const takeSessionFromAddress = () => {
  const ping = sessionFromAddress;
  sessionFromAddress = null;
  return ping;
};
